// AdminShipmentTransferScreen.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Paper,
    Snackbar,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import {
    useLazySearchDriversWithVehiclesForTransferQuery,
    useTransferLoadAndShipmentFromAdminMutation,
} from "store/services/driverApi";
import { useLazyGetShipmentDetailByDriverIdQuery } from "store/services/shipmentLoadingApi";
import AddVehicleModal from "../Driver/driverComponents/AddVehicleModal";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";

const INITIAL_STATE = { fromDriver: null, toDriver: null };
const ADD_VEHICLE_SENTINEL = { _id: "__ADD_VEHICLE__", __isAddVehicle__: true };

export const AdminShipmentTransferScreen = () => {
    const fromDriverDebounceRef = useRef(null);
    const toDriverDebounceRef = useRef(null);

    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
    const [selectedLoadings, setSelectedLoadings] = useState(new Set());
    const [selectedShipments, setSelectedShipments] = useState(new Set());
    const [selectedToVehicle, setSelectedToVehicle] = useState(null);
    const [addVehicleModalOpen, setAddVehicleModalOpen] = useState(false);
    const [extraDriverVehicles, setExtraDriverVehicles] = useState([]);
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [transferNote, setTransferNote] = useState("");

    const breadcrumbs = [
        { title: "Driver Transfer Requests", path: "/app/crm/load-transfer/list"},{title:"New Load Transfer"} ,
    ];

    // Two separate lazy query instances — each holds its own last-triggered result
    const [fetchFromDrivers, { data: fromDriverOptions, isLoading: isLoadingFromDrivers }] =
        useLazySearchDriversWithVehiclesForTransferQuery();
    const [fetchToDrivers, { data: toDriverOptions, isLoading: isLoadingToDrivers }] =
        useLazySearchDriversWithVehiclesForTransferQuery();

    const [fetchShipmentDetails, { data: shipmentData, isLoading: isLoadingShipments }] =
        useLazyGetShipmentDetailByDriverIdQuery();

    const [transferLoadAndShipmentFromAdmin, { isLoading: isTransferring }] =
        useTransferLoadAndShipmentFromAdminMutation();

    const { control, setValue } = useForm({ defaultValues: INITIAL_STATE, mode: "onChange" });
    const selectedFromDriver = useWatch({ control, name: "fromDriver" });
    const selectedToDriver = useWatch({ control, name: "toDriver" });

    useEffect(() => {
        if (selectedFromDriver?._id) {
            fetchShipmentDetails({ driverId: selectedFromDriver._id });
            setSelectedLoadings(new Set());
            setSelectedShipments(new Set());
        }
    }, [selectedFromDriver?._id, fetchShipmentDetails]);

    const handleFromDriverChange = (data, onChange) => {
        onChange(data);
        // If the new fromDriver is the same entity currently selected as toDriver, clear toDriver
        if (selectedToDriver?._id === data?._id) {
            setValue("toDriver", null);
            setSelectedToVehicle(null);
            setExtraDriverVehicles([]);
        }
        setSelectedLoadings(new Set());
        setSelectedShipments(new Set());
    };

    const handleToDriverChange = (data, onChange) => {
        onChange(data);
        setSelectedToVehicle(null);
        setExtraDriverVehicles([]);
    };

    const getUnitDisplay = (shipment) => {
        if (shipment?.weight) return `${shipment.weight} kg`;
        if (shipment?.unit?.value && shipment?.unit?.unitType?.name) {
            return shipment.unit.unitType.name.toLowerCase() === "weight"
                ? `${shipment.unit.value} kg`
                : `${shipment.unit.value} ${shipment.unit.unitType.name}`;
        }
        return "N/A";
    };

    const toVehicleOptions = useMemo(() => {
        const base = selectedToDriver?.assigned_vehicle_ids || [];
        return [...base, ...extraDriverVehicles, ADD_VEHICLE_SENTINEL];
    }, [selectedToDriver?.assigned_vehicle_ids, extraDriverVehicles]);

    const handleToVehicleChange = (_, data) => {
        if (!data) { setSelectedToVehicle(null); return; }
        if (data._id === ADD_VEHICLE_SENTINEL._id) { setAddVehicleModalOpen(true); return; }
        setSelectedToVehicle(data);
    };

    const handleAddVehicleSuccess = (newVehicle) => {
        setExtraDriverVehicles((prev) => [...prev, newVehicle]);
        setSelectedToVehicle(newVehicle);
        setAddVehicleModalOpen(false);
        setToast({
            open: true,
            severity: "success",
            message: `Vehicle ${newVehicle.registration_number} created and assigned to ${selectedToDriver.name}.`,
        });
    };

    const groupedByLoading = useMemo(() => {
        if (!shipmentData?.data?.shipments) return [];
        const groups = new Map();
        shipmentData.data.shipments.forEach((item) => {
            const key = item._id;
            if (!groups.has(key)) {
                groups.set(key, {
                    _id: item._id,
                    loadingId: item.loadingId,
                    loadingTime: item.loadingTime,
                    shipments: [],
                });
            }
            groups.get(key).shipments.push({
                shipmentId: item.shipmentId,
                weight: item.weight,
                senderName: item.senderName,
                unit: item.unit,
                receiverName: item.receiverName,
            });
        });
        return Array.from(groups.values());
    }, [shipmentData]);

    const handleLoadingSelect = (_id, shipments) => {
        const newSelectedLoadings = new Set(selectedLoadings);
        const newSelectedShipments = new Set(selectedShipments);
        if (selectedLoadings.has(_id)) {
            newSelectedLoadings.delete(_id);
            shipments.forEach((s) => newSelectedShipments.delete(`${_id}:${s.shipmentId}`));
        } else {
            newSelectedLoadings.add(_id);
            shipments.forEach((s) => newSelectedShipments.add(`${_id}:${s.shipmentId}`));
        }
        setSelectedLoadings(newSelectedLoadings);
        setSelectedShipments(newSelectedShipments);
    };

    const handleShipmentSelect = (_id, shipmentId) => {
        const key = `${_id}:${shipmentId}`;
        const newSelectedShipments = new Set(selectedShipments);
        const newSelectedLoadings = new Set(selectedLoadings);
        if (selectedShipments.has(key)) {
            newSelectedShipments.delete(key);
            newSelectedLoadings.delete(_id);
        } else {
            newSelectedShipments.add(key);
            const loading = groupedByLoading.find((g) => g._id === _id);
            const allSelected = loading?.shipments.every(
                (s) => newSelectedShipments.has(`${_id}:${s.shipmentId}`) || s.shipmentId === shipmentId
            );
            if (allSelected) newSelectedLoadings.add(_id);
        }
        setSelectedShipments(newSelectedShipments);
        setSelectedLoadings(newSelectedLoadings);
    };

    const getLoadingCheckboxState = (_id, shipments) => {
        const selectedCount = shipments.filter((s) =>
            selectedShipments.has(`${_id}:${s.shipmentId}`)
        ).length;
        if (selectedCount === 0) return { checked: false, indeterminate: false };
        if (selectedCount === shipments.length) return { checked: true, indeterminate: false };
        return { checked: false, indeterminate: true };
    };

    const selectedSummary = useMemo(() => ({
        loadingCount: selectedLoadings.size,
        shipmentCount: selectedShipments.size,
    }), [selectedLoadings, selectedShipments]);

    const handleSelectAll = () => {
        if (selectedLoadings.size === groupedByLoading.length) {
            setSelectedLoadings(new Set());
            setSelectedShipments(new Set());
        } else {
            const allLoadings = new Set(groupedByLoading.map((g) => g._id));
            const allShipments = new Set();
            groupedByLoading.forEach((group) => {
                group.shipments.forEach((s) => allShipments.add(`${group._id}:${s.shipmentId}`));
            });
            setSelectedLoadings(allLoadings);
            setSelectedShipments(allShipments);
        }
    };

    const handleTransferClick = () => {
        setNoteDialogOpen(true);
    };

    const handleNoteDialogClose = () => {
        setNoteDialogOpen(false);
        setTransferNote("");
    };

    const handleTransferConfirm = async () => {
        const result = await transferLoadAndShipmentFromAdmin({
            fromDriverId: selectedFromDriver._id,
            toDriverId: selectedToDriver._id,
            toVehicleId: selectedToVehicle._id,
            loadingIds: Array.from(selectedLoadings),
            note: transferNote.trim(),
        });

        if (result?.data?.success) {
            setToast({
                open: true,
                severity: "success",
                message: `Shipments transferred to ${selectedToDriver.name} successfully.`,
            });
            setSelectedLoadings(new Set());
            setSelectedShipments(new Set());
            setSelectedToVehicle(null);
            setNoteDialogOpen(false);
            setTransferNote("");
            fetchShipmentDetails({ driverId: selectedFromDriver._id });
        } else {
            setToast({
                open: true,
                severity: "error",
                message: result?.error?.data?.message || "Transfer failed. Please try again.",
            });
        }
    };

    // Client-side safety net: strip fromDriver from toDriver options after results load
    const filteredToDriverOptions = useMemo(() => {
        if (!toDriverOptions) return [];
        if (!selectedFromDriver?._id) return toDriverOptions;
        return toDriverOptions.filter((d) => d._id !== selectedFromDriver._id);
    }, [toDriverOptions, selectedFromDriver?._id]);

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Paper>
                <Grid container spacing={3}>
                    {/* Controls Row */}
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            {/* From Driver */}
                            <Box sx={{ flex: 1 }}>
                                <Controller
                                    name="fromDriver"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <BaseAutocomplete
                                            fullWidth
                                            label="Transfer From Driver"
                                            value={value ?? null}
                                            onChange={(_, data) => handleFromDriverChange(data, onChange)}
                                            onInputChange={(_, inputValue, reason) => {
                                                if (reason === "reset") return;
                                                clearTimeout(fromDriverDebounceRef.current);
                                                fromDriverDebounceRef.current = setTimeout(() => {
                                                    if (inputValue.trim()) fetchFromDrivers({
                                                        q: inputValue.trim(),
                                                        excludeId: selectedToDriver?._id,
                                                    });
                                                }, 300);
                                            }}
                                            getOptionLabel={(option) =>
                                                option ? `${option.name} (${option.driverId})` : ""
                                            }
                                            isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                                            options={fromDriverOptions || []}
                                            loading={isLoadingFromDrivers}
                                            loadingText="Searching drivers..."
                                        />
                                    )}
                                />
                            </Box>

                            {/* To Driver */}
                            <Box sx={{ flex: 1 }}>
                                <Controller
                                    name="toDriver"
                                    control={control}
                                    render={({ field: { value, onChange } }) => (
                                        <BaseAutocomplete
                                            fullWidth
                                            label="Transfer To Driver"
                                            value={value ?? null}
                                            onChange={(_, data) => handleToDriverChange(data, onChange)}
                                            onInputChange={(_, inputValue, reason) => {
                                                if (reason === "reset") return;
                                                clearTimeout(toDriverDebounceRef.current);
                                                toDriverDebounceRef.current = setTimeout(() => {
                                                    if (inputValue.trim()) fetchToDrivers({
                                                        q: inputValue.trim(),
                                                        excludeId: selectedFromDriver?._id,
                                                    });
                                                }, 300);
                                            }}
                                            getOptionLabel={(option) =>
                                                option ? `${option.name} (${option.driverId})` : ""
                                            }
                                            isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                                            options={filteredToDriverOptions}
                                            loading={isLoadingToDrivers}
                                            loadingText="Searching drivers..."
                                        />
                                    )}
                                />
                            </Box>

                            {/* To Vehicle — visible when toDriver is selected */}
                            {selectedToDriver && (
                                <Box sx={{ flex: 1 }}>
                                    <BaseAutocomplete
                                        fullWidth
                                        label="Transfer To Vehicle"
                                        value={selectedToVehicle ?? null}
                                        onChange={handleToVehicleChange}
                                        options={toVehicleOptions}
                                        getOptionLabel={(option) => {
                                            if (!option) return "";
                                            if (option._id === ADD_VEHICLE_SENTINEL._id) return "+ Add Vehicle";
                                            return `${option.vehicle_id} - ${option.registration_number}`;
                                        }}
                                        isOptionEqualToValue={(opt, val) => opt?._id === val?._id}
                                        renderOption={(props, option) => {
                                            if (option._id === ADD_VEHICLE_SENTINEL._id) {
                                                return (
                                                    <li {...props} key="__ADD_VEHICLE__">
                                                        <Typography variant="body2" color="primary" fontWeight={600}>
                                                            + Add Vehicle
                                                        </Typography>
                                                    </li>
                                                );
                                            }
                                            return (
                                                <li {...props} key={option._id}>
                                                    {option.vehicle_id} - {option.registration_number}
                                                </li>
                                            );
                                        }}
                                    />
                                </Box>
                            )}

                            {/* Transfer Button */}
                            <Box sx={{ pt: 0.5 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleTransferClick}
                                    disabled={
                                        !selectedFromDriver?._id ||
                                        !selectedToDriver?._id ||
                                        !selectedToVehicle?._id ||
                                        selectedLoadings.size === 0 ||
                                        isTransferring
                                    }
                                    sx={{ whiteSpace: "nowrap", height: 40 }}
                                >
                                    {isTransferring
                                        ? "Transferring..."
                                        : `Transfer Selected (${selectedSummary.shipmentCount})`}
                                </Button>
                            </Box>
                        </Stack>
                    </Grid>

                    {/* Summary */}
                    {shipmentData?.data && (
                        <Grid item xs={12}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                {[
                                    { label: "Total Loadings", value: shipmentData.data.totalLoadings },
                                    { label: "Total Shipments", value: shipmentData.data.totalShipments },
                                    { label: "Total Weight", value: `${shipmentData.data.totalWeight} kg` },
                                    { label: "Selected Loadings", value: selectedSummary.loadingCount, color: "primary.main" },
                                    { label: "Selected Shipments", value: selectedSummary.shipmentCount, color: "primary.main" },
                                ].map(({ label, value, color }) => (
                                    <Paper
                                        key={label}
                                        variant="outlined"
                                        sx={{
                                            px: 2, py: 1, minWidth: 120,
                                            textAlign: "center", borderRadius: 0,
                                            borderColor: "divider", bgcolor: "background.paper",
                                        }}
                                    >
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {label}
                                        </Typography>
                                        <Typography variant="subtitle1" fontWeight={600} color={color || "text.primary"}>
                                            {value}
                                        </Typography>
                                    </Paper>
                                ))}
                            </Stack>
                        </Grid>
                    )}

                    {/* Grouped Shipment Table */}
                    {isLoadingShipments ? (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">Loading shipments...</Typography>
                        </Grid>
                    ) : (
                        groupedByLoading.length > 0 && (
                            <Grid item xs={12}>
                                <TableContainer
                                    component={Paper}
                                    variant="outlined"
                                    sx={{ borderRadius: 0, borderColor: "divider", bgcolor: "background.paper" }}
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: "action.hover" }}>
                                                <TableCell padding="checkbox" sx={{ borderColor: "divider" }}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={
                                                                    selectedLoadings.size === groupedByLoading.length &&
                                                                    groupedByLoading.length > 0
                                                                }
                                                                indeterminate={
                                                                    selectedLoadings.size > 0 &&
                                                                    selectedLoadings.size < groupedByLoading.length
                                                                }
                                                                onChange={handleSelectAll}
                                                                size="small"
                                                            />
                                                        }
                                                        label={
                                                            <Typography variant="body2">
                                                                {selectedLoadings.size > 0
                                                                    ? `${selectedLoadings.size} selected`
                                                                    : "Select All"}
                                                            </Typography>
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: "text.primary", borderColor: "divider" }}>
                                                    Loading ID
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 700, color: "text.primary", borderColor: "divider" }}>
                                                    Shipments
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {groupedByLoading.map(({ _id, loadingId, shipments }) => {
                                                const checkboxState = getLoadingCheckboxState(_id, shipments);
                                                return (
                                                    <TableRow
                                                        key={_id}
                                                        sx={{ verticalAlign: "top", "&:last-child td": { borderBottom: 0 } }}
                                                    >
                                                        <TableCell padding="checkbox" sx={{ borderColor: "divider" }}>
                                                            <Checkbox
                                                                checked={checkboxState.checked}
                                                                indeterminate={checkboxState.indeterminate}
                                                                onChange={() => handleLoadingSelect(_id, shipments)}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ borderColor: "divider" }}>
                                                            <Typography
                                                                variant="caption"
                                                                fontWeight={600}
                                                                color="primary"
                                                                sx={{ fontFamily: "monospace" }}
                                                            >
                                                                {loadingId}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ borderColor: "divider" }}>
                                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                                                {shipments.map((shipment) => {
                                                                    const shipmentKey = `${_id}:${shipment.shipmentId}`;
                                                                    return (
                                                                        <Box
                                                                            key={shipment.shipmentId}
                                                                            sx={{
                                                                                display: "flex", alignItems: "center", gap: 1, p: 0.5,
                                                                                bgcolor: selectedShipments.has(shipmentKey)
                                                                                    ? "action.selected"
                                                                                    : "transparent",
                                                                            }}
                                                                        >
                                                                            <Checkbox
                                                                                checked={selectedShipments.has(shipmentKey)}
                                                                                onChange={() => handleShipmentSelect(_id, shipment.shipmentId)}
                                                                                size="small"
                                                                            />
                                                                            <Box sx={{ flex: 1 }}>
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        fontFamily: "monospace",
                                                                                        fontSize: "0.72rem",
                                                                                        fontWeight: 600,
                                                                                        color: "text.primary",
                                                                                    }}
                                                                                >
                                                                                    {shipment.shipmentId}
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    display="block"
                                                                                    color="text.secondary"
                                                                                    sx={{ fontSize: "0.65rem" }}
                                                                                >
                                                                                    From: {shipment.senderName} | To: {shipment.receiverName} | {getUnitDisplay(shipment)}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Grid>
                        )
                    )}

                    {/* Note Dialog */}
                    <Dialog open={noteDialogOpen} onClose={handleNoteDialogClose} fullWidth maxWidth="sm">
                        <DialogTitle>Transfer Note</DialogTitle>
                        <DialogContent>
                            <TextField
                                label="Note"
                                multiline
                                rows={4}
                                fullWidth
                                value={transferNote}
                                onChange={(e) => setTransferNote(e.target.value)}
                                sx={{ mt: 1 }}
                                required
                                helperText={
                                    transferNote.trim().length < 3
                                        ? `${3 - transferNote.trim().length} more character(s) required`
                                        : " "
                                }
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleNoteDialogClose}>Cancel</Button>
                            <Button
                                onClick={handleTransferConfirm}
                                variant="contained"
                                disabled={transferNote.trim().length < 3 || isTransferring}
                            >
                                {isTransferring ? "Transferring..." : "Confirm Transfer"}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    <AddVehicleModal
                        open={addVehicleModalOpen}
                        onClose={() => setAddVehicleModalOpen(false)}
                        onSuccess={handleAddVehicleSuccess}
                        driverId={selectedToDriver?._id}
                    />

                    <Snackbar
                        open={toast.open}
                        autoHideDuration={4000}
                        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    >
                        <Alert
                            severity={toast.severity}
                            variant="filled"
                            onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                        >
                            {toast.message}
                        </Alert>
                    </Snackbar>
                </Grid>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};